/**
 * API client.
 *
 * Same-origin, so no base URL and no CORS. Credentials are included because
 * the edit routes authenticate with the __Host- session cookie.
 */
export interface FieldProblem {
  readonly field: string
  readonly code: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problems: readonly FieldProblem[],
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  })

  if (response.status === 204) return undefined as T

  const payload = (await response.json().catch(() => ({}))) as {
    problems?: FieldProblem[]
    error?: { message?: string }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload.problems ?? [],
      payload.error?.message ?? 'Something went wrong. Try again.',
    )
  }
  return payload as T
}

export interface DraftInput {
  readonly siteId?: string | undefined
  readonly name: string
  readonly tagline: string
  readonly slug: string
  readonly launchAt: string
  readonly timeZone: string
  readonly windowId: string
  readonly theme: unknown
  readonly waitlist: boolean
}

export function saveDraft(input: DraftInput) {
  return call<{ siteId: string; slug: string }>('/api/draft', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function startCheckout(siteId: string, windowId: string) {
  return call<{ url: string }>('/api/checkout', {
    method: 'POST',
    body: JSON.stringify({ siteId, windowId }),
  })
}

/**
 * Turn server-side field problems into the same copy the client validator uses.
 *
 * The server answers with codes rather than prose so the two cannot drift into
 * saying different things about the same rule.
 */
export const PROBLEM_MESSAGES: Readonly<Record<string, string>> = {
  taken: 'That address is already taken. Try another.',
  reserved: 'That name is reserved.',
  'too-short': 'That address is too short.',
  'too-long': 'Keep it shorter.',
  'invalid-characters': 'Letters, numbers and hyphens only.',
  required: 'This is required.',
  invalid: 'That does not look right.',
  'in-past': 'Pick a date in the future.',
  'after-expiry': 'That is past the end of your hosting window.',
  profanity: "That wording won't work here.",
  'brand-impersonation':
    'That looks like it could be confused with an existing company. Pick something distinct.',
  'phishing-pattern': 'That combination of words is one we cannot publish.',
  'high-risk-term': 'That combination of words is one we cannot publish.',
}

export function problemMessage(problem: FieldProblem): string {
  return PROBLEM_MESSAGES[problem.code] ?? 'That does not look right.'
}
